// The Sin Offering tab's presentation decisions, in one place and with no JSX, so
// Node's own test runner can import them under --experimental-strip-types.
//
// WHY ANY OF THIS IS HERE RATHER THAN INLINE. Three of these functions decide what
// a person is told about money charged against them, and getting one wrong is not
// a cosmetic defect. `settlementSentence` is the sentence that says nothing has
// been deducted; if it ever renders as though something has, the screen is lying
// about somebody's pay. web/test/sinModel.test.ts pins all three.
//
// NOTHING HERE COMPUTES A CHARGE. The amount, the rules, the clean slate and the
// cap all live in Postgres per CLAUDE.md rule 4, and this file only formats what
// comes back. If you find yourself needing an amount that the server did not send,
// that is a view or a function, not a helper.
//
// ONE IMPORT, AND IT IS TYPE-ONLY, WHICH IS NOT A STYLE CHOICE. Node's ESM loader
// under --experimental-strip-types resolves specifiers literally and cannot
// resolve this repository's extensionless relative imports. A `import type` is
// erased before resolution and costs nothing; a value import of
// ../cash/cashModel would make this file unloadable by its own test, which is
// the same trap CLAUDE.md describes for api/src/handlers importing runtime.
//
// So `zar` below is a deliberate second copy of the Driver Cash formatter rather
// than a reuse of it. Both ask the en-ZA locale for the same two decimal places,
// and web/test/sinModel.test.ts pins the behaviour rather than the glyphs so an
// ICU difference cannot fail the build. If the two ever need to disagree about
// what a rand looks like, something has gone wrong upstream of both.
import type { ChipTone } from '../interviews/Chip'

/** Rands, en-ZA, and a dash for a figure that is not known. */
export function zar(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const body = Math.abs(n).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${n < 0 ? '-' : ''}R${body}`
}

/**
 * A date as a person says it, from whatever the server sent.
 *
 * WHY THIS EXISTS. counts_from is a Postgres `date`, and postgres.js hands a
 * date back as a JS Date, which JSON.stringify turns into
 * "2026-09-01T00:00:00.000Z". Interpolated straight into a sentence that reads
 * "nothing dated before X counts, ever", that renders a machine timestamp in the
 * one sentence on the page that tells somebody which of their missed dates can
 * cost them money. It shipped that way on 1 September 2026 and was caught on the
 * screen rather than by the gate, which is CLAUDE.md section 11 again.
 *
 * Parsed off the leading ten characters rather than through `new Date`, because a
 * date-only value has no timezone and letting the browser apply one can move it a
 * day: at UTC+2 that is the difference between a task being inside the clean
 * slate and outside it.
 */
export function dateLabel(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '\u2014'
  const iso = value instanceof Date ? value.toISOString() : String(value)
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return String(value)
  const months = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December']
  const month = months[Number(m[2]) - 1]
  if (!month) return String(value)
  return `${Number(m[3])} ${month} ${m[1]}`
}

export type SinStatus =
  | 'Pending approval'
  | 'Approved'
  | 'Declined'
  | 'Withdrawn'
  | 'Waived'

export type SinRow = {
  id: string
  person_id: string
  subject_name: string | null
  task_ref: number | null
  task_title: string | null
  reason_code: 'task_overdue' | 'task_undated' | 'manual' | 'incident_update_overdue'
  reason_label: string
  reason: string | null
  amount_zar: number | string
  period_start: string
  settlement_route: 'payslip' | 'invoice'
  origin: 'self' | 'other' | 'system'
  status: SinStatus
  raised_by_name: string | null
  raised_at: string
  decision_note: string | null
  representation: string | null
  has_representation: boolean
  settled: boolean
  // Migration 1600: an incident update missed. These never go to payroll while
  // ledger_only is true, and settle by being marked paid instead.
  incident_id?: string | null
  incident_registration?: string | null
  missed_date?: string | null
  paid_outside_payroll?: boolean
  paid_outside_payroll_by_name?: string | null
  ledger_only?: boolean
}

export type SinPolicy = {
  amount_zar: number | string
  counts_from: string | Date
  overdue_rule_active: boolean
  undated_rule_active: boolean
  undated_grace_hours: number
  undated_counts_start_date: boolean
  require_human_set_target: boolean
  charge_delegator_when_unassigned: boolean
  per_task_once: boolean
  target_date_gate_active: boolean
  bcea_cap_fraction: number | string
  payroll_release_enabled: boolean
  legal_signoff_ref: string | null
}

/**
 * The cap, or the honest absence of one.
 *
 * CLAUDE.md section 1 rule 6: a missing figure shows as missing, never as zero.
 * Remuneration is not on file for anybody, so this returns null and the screen
 * has to say "not known" rather than printing R0.00, which would read as
 * "no deduction is permitted" when it actually means "nobody has told us".
 */
export function capLabel(cap: number | string | null | undefined): string {
  if (cap === null || cap === undefined || cap === '') return 'Not known'
  return zar(cap)
}

export function statusTone(status: SinStatus): ChipTone {
  switch (status) {
    case 'Approved':
      return 'red'
    case 'Pending approval':
      return 'amber'
    case 'Waived':
      return 'green'
    default:
      return 'neutral'
  }
}

/**
 * What has actually happened to the money on this row.
 *
 * The distinction this exists to hold: an APPROVED charge has moved nothing. A
 * row reading "Approved" beside a rand amount invites a reader to believe it came
 * off somebody's pay, and none of them have. Only `settled` means money moved,
 * and nothing sets settled today.
 */
export function settlementSentence(
  row: Pick<SinRow, 'status' | 'settled' | 'settlement_route'> &
    Partial<Pick<SinRow, 'paid_outside_payroll' | 'ledger_only'>>,
): string {
  // Paid by hand is checked before settled, because a ledger-only charge is
  // never deducted and must never read as though it was.
  if (row.paid_outside_payroll) {
    return 'Paid outside payroll. Nothing was deducted.'
  }
  if (row.settled) {
    return `Deducted from ${row.settlement_route === 'payslip' ? 'the payslip' : 'the invoice'}.`
  }
  switch (row.status) {
    case 'Approved':
      return row.ledger_only
        ? 'Approved and owed. Ledger only: it is paid outside payroll and never deducted.'
        : 'Approved and recorded. Nothing has been deducted.'
    case 'Pending approval':
      return 'Waiting on a decision. Nothing has been deducted.'
    case 'Waived':
      return 'Waived. Nothing is owed.'
    case 'Declined':
      return 'Declined. Nothing is owed.'
    case 'Withdrawn':
      return 'Withdrawn. Nothing is owed.'
    default:
      return 'Nothing has been deducted.'
  }
}

/**
 * Whether a charge still needs the person's own written answer.
 *
 * True is the interesting case and the reason the column exists: an approved
 * charge with no answer on it is one that has not had the fair opportunity to
 * make representations that BCEA s34(2)(b) requires, and the release refuses
 * while any of them exist.
 */
export function awaitingAnswer(row: Pick<SinRow, 'status' | 'has_representation'>): boolean {
  return (row.status === 'Approved' || row.status === 'Pending approval') && !row.has_representation
}

/** Who raised it, said the way a person would say it. */
export function originLabel(origin: SinRow['origin'], raisedBy: string | null): string {
  switch (origin) {
    case 'system':
      return 'Raised by the automatic rules'
    case 'self':
      return `Raised and approved by ${raisedBy ?? 'the approver'}`
    default:
      return `Raised by ${raisedBy ?? 'somebody'}`
  }
}

/**
 * The banner, or nothing at all.
 *
 * NULL IS THE NORMAL STATE AND THAT IS THE POINT. Luyanda asked on 1 September
 * 2026 for the standing BCEA paragraph to come off this page, and it has. A tab
 * that opens with a paragraph of labour law reads as though it is apologising for
 * existing, and the people who see it already know what it is for.
 *
 * WHAT WAS LOST, AND WHERE IT STILL IS, because none of it may quietly go
 * missing:
 *
 *   "nothing has been deducted"  is on the Actually deducted card, which reads
 *                                R0.00 with "Nothing has moved" under it, and on
 *                                every single row through settlementSentence.
 *   the reason a deduction is    is in sin_offering_release_v1, which refuses and
 *   not lawful yet               says why in full. It arrives at the moment
 *                                somebody presses Settle, which is when the
 *                                reason is actually wanted rather than every time
 *                                the page opens.
 *
 * So this now returns null in the ordinary case and a banner ONLY when something
 * is genuinely off: no policy at all, or a live money path. Those two are
 * operational warnings about the state of the system, not commentary on the law,
 * and neither one is a paragraph.
 */
export function releaseBanner(
  policy: SinPolicy | null,
): { tone: ChipTone; text: string } | null {
  if (!policy) {
    return {
      tone: 'amber',
      text: 'There is no Sin Offering policy in force, so no amount is set and nothing can be raised.',
    }
  }
  // Settlement is off, which is the normal state. The cards and the rows already
  // say nothing has moved, so there is nothing here worth a banner.
  if (!policy.payroll_release_enabled) return null

  if (!policy.legal_signoff_ref || !policy.legal_signoff_ref.trim()) {
    return {
      tone: 'amber',
      text:
        'Settlement is switched on but no sign-off is recorded against the policy, so a release ' +
        'will still refuse. Put the reference on the policy row.',
    }
  }
  return {
    tone: 'red',
    text:
      `Settlement is open under ${policy.legal_signoff_ref.trim()}. A release still refuses for anybody ` +
      'whose remuneration is not on file, whose approved charges exceed a quarter of it, or who has not ' +
      'answered a charge in writing.',
  }
}

/** The rules as they currently stand, in a sentence each, for the policy panel. */
export function ruleSentences(policy: SinPolicy | null): string[] {
  if (!policy) return []
  const out: string[] = [
    `The amount is ${zar(policy.amount_zar)}.`,
    `Nothing dated before ${dateLabel(policy.counts_from)} counts, ever.`,
  ]
  out.push(
    policy.overdue_rule_active
      ? 'A task past its target date is charged once.'
      : 'The overdue rule is switched off.',
  )
  if (policy.undated_rule_active) {
    out.push(
      `A task older than ${policy.undated_grace_hours} hours with no target date` +
        (policy.undated_counts_start_date ? ', or no start date,' : '') +
        ' is charged once.',
    )
  } else {
    out.push('The undated rule is switched off.')
  }
  if (policy.require_human_set_target) {
    out.push('A target date set by an import or a scheduled job is never charged against anybody.')
  }
  if (policy.charge_delegator_when_unassigned) {
    out.push('An undated task with nobody on it is charged to whoever created it.')
  }
  out.push(
    policy.per_task_once
      ? 'Once per task per rule, never per day.'
      : 'REPEATED CHARGES ARE ON: the same task can be charged more than once for the same rule.',
  )
  out.push(
    policy.target_date_gate_active
      ? 'A target date is set once. Changing one already set needs a reason and an approval.'
      : 'The target date gate is OFF: a date already set can still be changed directly on the board.',
  )
  return out
}
